import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "sonner";
import axiosInstance from "../../interceptors";
import { baseUrl } from "../../config/config";

// Forced / self-service change password.
// Reached either right after first login (server flags mustChangePassword and
// the interceptor blocks every other call with 403 PASSWORD_CHANGE_REQUIRED),
// or proactively from the auth guard's password-state check.
//
// API: POST /common/tenant/users/me/change-password
//   { currentPassword, newPassword, confirmNewPassword }

const ACCESS_KEY = "e_access_token";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Change password - Ample Hire";
    const token = localStorage.getItem(ACCESS_KEY) || Cookies.get(ACCESS_KEY);
    if (!token) navigate("/signin", { replace: true });
  }, [navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.currentPassword) return "Enter your current (temporary) password.";
    if (!form.newPassword || form.newPassword.length < 8) return "New password must be at least 8 characters.";
    if (form.newPassword === form.currentPassword) return "New password must be different from the current password.";
    if (form.newPassword !== form.confirmNewPassword) return "New password and confirmation do not match.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    setError(null);
    try {
      const r = await axiosInstance.post(`${baseUrl}/common/tenant/users/me/change-password`, form);
      if (r.data?.success === false) {
        throw new Error(r.data?.message || "Failed to update password.");
      }
      // Clear the one-shot gate so the auth guard won't bounce us back here.
      sessionStorage.setItem("pw_state_checked", "ok");
      toast.success(r.data?.message || "Password updated successfully.");
      navigate("/admin/dashboard-v2", { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to update password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-vf-bg px-4 font-sans">
      <div className="w-full max-w-md">
        <div className="card animate-scale-in">
          <div className="mb-5">
            <div className="w-11 h-11 rounded-card bg-vf-primary text-white flex items-center justify-center mb-4">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-vf-base font-heading">Set a new password</h1>
            <p className="text-sm text-vf-muted mt-1">
              For your security, please replace the temporary password you were emailed with one of your own.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="input-label">Current (temporary) password</span>
              <input
                type="password" className="input" autoComplete="current-password" autoFocus
                value={form.currentPassword} onChange={(e) => set("currentPassword", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="input-label">New password</span>
              <input
                type="password" className="input" autoComplete="new-password" placeholder="At least 8 characters"
                value={form.newPassword} onChange={(e) => set("newPassword", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="input-label">Confirm new password</span>
              <input
                type="password" className="input" autoComplete="new-password"
                value={form.confirmNewPassword} onChange={(e) => set("confirmNewPassword", e.target.value)}
              />
            </label>

            {error && <div className="alert-danger">{error}</div>}

            <button type="submit" disabled={saving} className="btn-primary w-full justify-center">
              {saving ? "Updating…" : "Update password"}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-vf-muted mt-4">
          Signed in with the wrong account?{" "}
          <button
            type="button"
            className="text-vf-primary font-medium"
            onClick={() => { localStorage.clear(); navigate("/signin", { replace: true }); }}
          >
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
};

export default ChangePassword;
